import React, { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Save, Globe, Mail, FileText, PackageOpen } from 'lucide-react'
import { systemSettingsApi, DEFAULT_SYSTEM_SETTINGS } from '../../lib/api'
import SecuritySettingsCard from './SecuritySettingsCard'
import LoadingState from '../../components/shared/LoadingState'
import ErrorState from '../../components/shared/ErrorState'
import { useToast } from '../../hooks/useToast'
import { usePermission } from '../../hooks/usePermission'
import type { SystemSettings } from '../../types'
import SmtpSettingsCard from './SmtpSettingsCard'
import { tx } from '../../lib/i18n'

const ToggleRow: React.FC<{ label: string; description: string; value: boolean; onChange: (v: boolean) => void; danger?: boolean }> = ({ label, description, value, onChange, danger }) => (
  <div className="flex items-start justify-between py-4 border-b border-gray-50 last:border-0">
    <div className="flex-1 min-w-0 mr-4">
      <p className={`text-sm font-medium ${danger && value ? 'text-red-700' : 'text-gray-800'}`}>{label}</p>
      <p className="text-xs text-gray-400 mt-0.5">{description}</p>
    </div>
    <button
      onClick={() => onChange(!value)}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none flex-shrink-0 ${
        value ? (danger ? 'bg-red-500' : 'bg-indigo-600') : 'bg-gray-200'
      }`}
    >
      <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${value ? 'translate-x-6' : 'translate-x-1'}`} />
    </button>
  </div>
)

const SystemSettingsPage: React.FC = () => {
  const queryClient = useQueryClient()
  const { showToast } = useToast()
  const { can } = usePermission()

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['system-settings'],
    queryFn: () => systemSettingsApi.get().then((r) => r.data.data),
  })

  const [form, setForm] = useState<SystemSettings>(data ?? DEFAULT_SYSTEM_SETTINGS)

  useEffect(() => {
    if (data) setForm(data)
  }, [data])

  const saveMutation = useMutation({
    mutationFn: () => systemSettingsApi.update(form),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['system-settings'] })
      showToast({ type: 'success', message: tx('Settings saved successfully.') })
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (e: any) => showToast({ type: 'error', message: e?.response?.data?.error ?? 'Failed to save settings.' }),
  })

  const set = <K extends keyof SystemSettings>(key: K, value: SystemSettings[K]) =>
    setForm((f) => ({ ...f, [key]: value }))

  const settings = form

  if (isLoading) return <LoadingState variant="spinner" message={tx('Loading settings...')} />
  if (error && !data) return <ErrorState message={tx('Failed to load settings.')} onRetry={() => refetch()} />

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">{tx('System Settings')}</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {tx("Settings for the whole platform · General / Contact / Plans are saved but not yet used by queza-core (no effect on shops)")}
          </p>
        </div>
        {can('system-settings.update') && (
        <button
          onClick={() => saveMutation.mutate()}
          disabled={saveMutation.isPending}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
        >
          <Save className="h-4 w-4" />
          {saveMutation.isPending ? tx('Saving...') : tx('Save Settings')}
        </button>
        )}
      </div>


      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {/* General Settings */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
          <div className="flex items-center gap-2 mb-4">
            <div className="p-2 bg-indigo-100 rounded-lg">
              <Globe className="h-4 w-4 text-indigo-600" />
            </div>
            <h2 className="text-sm font-semibold text-gray-800">{tx('General')}</h2>
          </div>
          <ToggleRow
            label={tx('Maintenance Mode')}
            description={tx('When enabled, all shops see a maintenance page')}
            value={settings.maintenanceMode}
            onChange={(v) => set('maintenanceMode', v)}
            danger
          />
          <ToggleRow
            label={tx('New Signups Enabled')}
            description={tx('Allow new shops to register on the platform')}
            value={settings.signupEnabled}
            onChange={(v) => set('signupEnabled', v)}
          />
          <div className="mt-4 space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{tx('Trial Days')}</label>
              <input
                type="number"
                min="1"
                max="90"
                value={settings.trialDays}
                onChange={(e) => set('trialDays', parseInt(e.target.value))}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{tx('Default Currency')}</label>
              <select
                value={settings.defaultCurrency}
                onChange={(e) => set('defaultCurrency', e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="THB">{tx('THB — Thai Baht')}</option>
                <option value="USD">{tx('USD — US Dollar')}</option>
                <option value="SGD">SGD — Singapore Dollar</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{tx('VAT Rate (%)')}</label>
              <input
                type="number"
                min="0"
                max="30"
                step="0.5"
                value={settings.vatRate}
                onChange={(e) => set('vatRate', parseFloat(e.target.value))}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>
        </div>

        {/* Contact & Legal */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
          <div className="flex items-center gap-2 mb-4">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Mail className="h-4 w-4 text-blue-600" />
            </div>
            <h2 className="text-sm font-semibold text-gray-800">{tx('Contact & Legal')}</h2>
          </div>
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{tx('Support Email')}</label>
              <input
                type="email"
                value={settings.supportEmail}
                onChange={(e) => set('supportEmail', e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{tx('Terms of Service URL')}</label>
              <input
                type="url"
                value={settings.termsUrl}
                onChange={(e) => set('termsUrl', e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="https://"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{tx('Privacy Policy URL')}</label>
              <input
                type="url"
                value={settings.privacyUrl}
                onChange={(e) => set('privacyUrl', e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="https://"
              />
            </div>
          </div>
        </div>

        {/* Plans */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
          <div className="flex items-center gap-2 mb-4">
            <div className="p-2 bg-purple-100 rounded-lg">
              <PackageOpen className="h-4 w-4 text-purple-600" />
            </div>
            <h2 className="text-sm font-semibold text-gray-800">{tx('Plans')}</h2>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{tx('Default Plan for New Signups')}</label>
            <select
              value={settings.defaultPlanId}
              onChange={(e) => set('defaultPlanId', e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="starter">Starter</option>
              <option value="pro">Pro</option>
              <option value="business">Business</option>
            </select>
          </div>
        </div>

        {/* Admin sign-in security (2FA) */}
        <SecuritySettingsCard />

        {/* Email (SMTP) — own save button, stored separately */}
        <SmtpSettingsCard />

        {/* Maintenance banner preview */}
        {settings.maintenanceMode && (
          <div className="md:col-span-2 bg-red-50 border border-red-200 rounded-xl p-5">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <FileText className="h-4 w-4 text-red-600" />
              </div>
              <div>
                <p className="text-sm font-semibold text-red-800">{tx('Maintenance Mode is ACTIVE')}</p>
                <p className="text-sm text-red-600 mt-0.5">{tx('All shop users will see a maintenance page until this is disabled.')}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default SystemSettingsPage
